import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import withRouter from "../../components/Common/withRouter";
import { createSelector } from "reselect";
import moment from 'moment'
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Alert,
} from "reactstrap";

import GlobalLoader from "../../components/Common/GlobalLoader";
import TableContainerWarehouseRx from "../../components/Common/TableContainerWarehouseRx";

import {
  loadBackupHistory as onLoadBackupHistory,
  recordBackup,
  downloadBackup as onDownloadBackup,
} from "../../slice/thunks";
import "./backup.css";
import { parseDate } from "../../components/Common/Utilities";

const ListBackup = (props) => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // 🔹 States pour le modal
  const [isBackupModalOpen, setIsBackupModalOpen] = useState(false);
  const [sendByEmail, setSendByEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // 🔹 Redux selector aligné avec deductionCategories
  const selectBackupState = (state) => state.Payroll;
  const backupSelector = createSelector(selectBackupState, (payroll) => ({
    backups: payroll?.backupHistory ?? {
      items: [],
      size: 0,
      page: 1,
      total_items: 0,
      total_page: 1,
    },
    error: payroll?.error ?? null,
  }));

  const downloadSelector = createSelector(selectBackupState, (payroll) => ({
    downloadResponse: payroll?.body ?? null,
    error: payroll?.error ?? null,
  }));

  const { backups, error } = useSelector(backupSelector);
  const { downloadResponse, downloadError } = useSelector(downloadSelector);

  // 🔹 Charger les backups
  const loadBackups = useCallback(
    (page = 1) => {
      const config = {
        page,
        size: pageSize,
      };
      setIsLoading(true);
      dispatch(onLoadBackupHistory(config))
        .unwrap()
        .then((response) => {
        })
        .finally(() => setIsLoading(false));

      setCurrentPage(page);
    },
    [dispatch, pageSize]
  );

  const downloadBackup = useCallback((backupId, filename) => {
    dispatch(onDownloadBackup({ backupId: backupId }))
    .unwrap()
    .then((response) => {
      if (response) {
        const url = window.URL.createObjectURL(response);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        console.error("URL de téléchargement manquante dans la réponse");
      }
    })
    .catch((err) => {
      console.error("Erreur lors du téléchargement du backup:", err);
    });
  }, [dispatch]);

  useEffect(() => {
    loadBackups();
  }, [loadBackups]);

  // 🔹 Colonnes
  const columns = useMemo(
    () => [
      {
        header: "#",
        cell: ({ row }) => row.index + 1 + (currentPage - 1) * pageSize,
        enableColumnFilter: false,
      },
      {
        header: props.t("file_name"),
            accessorFn: (row) =>
          row.fileName ? row.fileName.split("/").pop() : "-",
        cell: ({ row }) => {
          const fullPath = row.original.fileName || "-";
          const shortName = fullPath.split("/").pop();
          return <span title={fullPath}>{shortName}</span>;
        },
        enableColumnFilter: false,
      },
      {
        header: props.t("created_at"),
        enableColumnFilter: false,
        cell: (cell) => {
          return <span>
            {cell.row.original.createdAt}
            {/* {moment(cell.row.original.createdAt).isValid() ? parseDate(cell.row.original.createdAt) : "-"} */}
          </span>
        }
      },
      {
        header: props.t("file_size"),
          accessorFn: (row) => {
          if (!row.fileSize) return "-";
          const sizeMB = row.fileSize / (1024 * 1024);
          return sizeMB >= 1
            ? sizeMB.toFixed(1) + " MB"
            : (row.fileSize / 1024).toFixed(1) + " KB";
        },
        enableColumnFilter: false,
      },
      {
        header: props.t("Action"),
        // accessorFn: (row) => (row.auto ? props.t("yes") : props.t("no")),
        accessorFn: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const fullPath = row.original.fileName || "-";
          const shortName = fullPath.split("/").pop()
          return (
            <Button
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-outline-primary download-btn"
              onClick={() => downloadBackup(row.original.id, shortName)}
            >
              {props.t("Download")}
            </Button>
          );
        }},
    ],
    [props.t, currentPage, pageSize]
  );

  // 🔹 Gérer la création du backup
  const handleConfirmBackup = async () => {
    try {
      setIsLoading(true);
      await dispatch(recordBackup({ sendByEmail, email })).unwrap();
      setIsBackupModalOpen(false);
      setSuccessMessage(props.t("backup_created_successfully"));
      loadBackups(currentPage);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("Erreur backup:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6>{props.t("list_backups")}</h6>
        <Button color="primary" onClick={() => setIsBackupModalOpen(true)} className="btns">
          <i className="fa fa-plus me-2"></i>
          {props.t("create_backup")}
        </Button>
      </div>

      {error && (
        <div className="alert alert-danger">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}
      {successMessage && <Alert color="success">{successMessage}</Alert>}

      <div className="card table-card p-2">
        {isLoading ? (
          <GlobalLoader />
        ) : (
          <TableContainerWarehouseRx
            columns={columns}
            data={backups.items ?? []}
            tableSize={backups.size ?? pageSize}
            tablePage={backups.page ?? currentPage}
            tableTotal={backups.total_items ?? 0}
            tableTotalPage={backups.total_page ?? 1}
            onNextPage={(page) => loadBackups(page)}
          />
        )}
      </div>

      {/* 🔹 Modal de confirmation */}
      <Modal
        isOpen={isBackupModalOpen}
        toggle={() => setIsBackupModalOpen(!isBackupModalOpen)}
        centered
      >
        <ModalHeader toggle={() => setIsBackupModalOpen(false)}>
          {props.t("confirm_backup")}
        </ModalHeader>
        <ModalBody>
          <p>{props.t("are_you_sure_backup")}</p>
          <FormGroup check>
            <Input
              type="checkbox"
              id="sendByEmail"
              checked={sendByEmail}
              onChange={(e) => setSendByEmail(e.target.checked)}
            />
            <Label for="sendByEmail" check>
              {props.t("send_backup_by_email")}
            </Label>
          </FormGroup>
          {sendByEmail && (
            <FormGroup className="mt-2">
              <Label for="backupEmail">{props.t("email")}</Label>
              <Input
                type="email"
                id="backupEmail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={props.t("enter_email")}
              />
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleConfirmBackup}>
            {props.t("confirm")}
          </Button>
          <Button
            color=""
            onClick={() => setIsBackupModalOpen(false)}
            className="btn-danger"
          >
            {props.t("cancel")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default withRouter(withTranslation()(ListBackup));
